import { lazy, Suspense, useEffect, useRef, type ComponentType } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from '@/hooks/useStore';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { authApi } from '@/api';
import { ApiError } from '@/api/client';
import { ThemeProvider } from '@/contexts/ThemeContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import Layout from '@/components/layout/Layout';
import ToastContainer from '@/components/ui/ToastContainer';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { sanitizeAuthReturnPath, startRetryingNativeOAuthCallbacks } from '@/utils/googleOAuth';
import { clientLogger } from '@/utils/clientLogger';
import { routeRoles, type UserRole } from '@/config/navigation';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Isolate server-state caches whenever auth ownership changes accounts. */
export function installAuthQueryIsolation(client: QueryClient): () => void {
  let previousUserId = useAuthStore.getState().user?.id ?? null;

  return useAuthStore.subscribe((state) => {
    const nextUserId = state.user?.id ?? null;
    if (nextUserId === previousUserId) return;

    previousUserId = nextUserId;
    // Cancellation is triggered before removal so cooperative query functions
    // stop work; clearing prevents the next account from rendering old data.
    void client.cancelQueries();
    client.clear();
  });
}

function AuthQueryIsolation() {
  useEffect(() => installAuthQueryIsolation(queryClient), []);
  return null;
}

// Retry dynamic imports to handle stale chunks after deployments
function lazyWithRetry(importFn: () => Promise<{ default: ComponentType }>) {
  return lazy(() =>
    importFn().catch(() => {
      // Retry once after a brief delay — the browser may fetch the updated chunk
      return new Promise<{ default: ComponentType }>((resolve, reject) => {
        setTimeout(() => {
          importFn().then(resolve, reject);
        }, 1000);
      });
    }),
  );
}

// Lazy-loaded page components for code splitting
const Login = lazyWithRetry(() => import('@/pages/Login'));
const ForgotPassword = lazyWithRetry(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('@/pages/ResetPassword'));
const SetPassword = lazyWithRetry(() => import('@/pages/SetPassword'));
const OAuthCallback = lazyWithRetry(() => import('@/pages/OAuthCallback'));
const PrivacyPolicy = lazyWithRetry(() => import('@/pages/PrivacyPolicy'));
const Support = lazyWithRetry(() => import('@/pages/Support'));
const Dashboard = lazyWithRetry(() => import('@/pages/Dashboard'));
const MyTasks = lazyWithRetry(() => import('@/pages/MyTasks'));
const Projects = lazyWithRetry(() => import('@/pages/Projects'));
const ProjectView = lazyWithRetry(() => import('@/pages/project-view'));
const ProjectAutomation = lazyWithRetry(() => import('@/pages/ProjectAutomation'));
const Teams = lazyWithRetry(() => import('@/pages/Teams'));
const SearchPage = lazyWithRetry(() => import('@/pages/Search'));
const Settings = lazyWithRetry(() => import('@/pages/Settings'));
const Notifications = lazyWithRetry(() => import('@/pages/Notifications'));
const FormBuilder = lazyWithRetry(() => import('@/pages/FormBuilder'));
const FormSubmit = lazyWithRetry(() => import('@/pages/FormSubmit'));
const ActivityFeed = lazyWithRetry(() => import('@/pages/ActivityFeed'));
const RecurringTasks = lazyWithRetry(() => import('@/pages/RecurringTasks'));
const Credentials = lazyWithRetry(() => import('@/pages/credentials'));
const TaskDeepLink = lazyWithRetry(() => import('@/pages/TaskDeepLink'));
const VendorWorkspace = lazyWithRetry(() => import('@/pages/vendors'));

function PageLoader() {
  return (
    <div
      className="flex-1 flex items-center justify-center"
      role="status"
      aria-label="Loading page"
    >
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: readonly UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, token, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated || !token || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If allowedRoles is specified, check if user's role is permitted
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, token, user } = useAuthStore();
  const location = useLocation();

  if (isAuthenticated && token && user) {
    const from = (
      location.state as {
        from?: { pathname?: string; search?: string; hash?: string };
      } | null
    )?.from;
    const returnPath = sanitizeAuthReturnPath(
      from?.pathname ? `${from.pathname}${from.search || ''}${from.hash || ''}` : '/',
    );
    return <Navigate to={returnPath} replace />;
  }

  return <>{children}</>;
}

/** Validate persisted auth without allowing a stale request to own a newer login. */
export async function bootstrapAuthSession(
  validateSession: typeof authApi.me = authApi.me,
): Promise<void> {
  const captured = useAuthStore.getState();
  const capturedToken = captured.token;
  const capturedUserId = captured.user?.id ?? null;
  if (!capturedToken) {
    if (captured.user || captured.isAuthenticated) captured.clearAuth();
    return;
  }

  // A complete persisted identity and bearer are authoritative enough to keep
  // the shell usable during a transient validation outage.
  if (captured.user && !captured.isAuthenticated) {
    captured.setAuth(captured.user, capturedToken);
  }

  try {
    const response = await validateSession();
    const current = useAuthStore.getState();
    const stillOwnsValidation =
      current.token === capturedToken ||
      (capturedUserId !== null && current.user?.id === capturedUserId);
    const responseMatchesOwner = capturedUserId === null || response.user.id === capturedUserId;

    if (!current.token || !stillOwnsValidation || !responseMatchesOwner) return;

    // The API client may have rotated the bearer while /me was in flight. Keep
    // that current token instead of restoring the captured, revoked token.
    current.setAuth(response.user, current.token);
  } catch (error) {
    // A transport outage or server-side 5xx does not prove that the persisted
    // bearer is invalid. Keep the exact local owner so the app can recover
    // when connectivity returns; protected API requests will still clear it
    // when the server provides a terminal auth/account response.
    const terminalStatus = error instanceof ApiError ? error.status : null;
    if (terminalStatus !== 401 && terminalStatus !== 403 && terminalStatus !== 404) return;

    const current = useAuthStore.getState();
    const sameCapturedOwner = (current.user?.id ?? null) === capturedUserId;
    if (current.token === capturedToken && sameCapturedOwner) {
      current.clearAuth();
    }
  }
}

function AppRoutes() {
  const navigate = useNavigate();
  const nativeOAuthNavigateRef = useRef(navigate);
  const nativeOAuthRegistrationRef = useRef<{
    leases: number;
    cleanup: (() => Promise<void>) | null;
    cleanupTimer: ReturnType<typeof setTimeout> | null;
  }>({ leases: 0, cleanup: null, cleanupTimer: null });
  nativeOAuthNavigateRef.current = navigate;
  usePushNotifications(navigate);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const registration = nativeOAuthRegistrationRef.current;
    registration.leases += 1;
    if (registration.cleanupTimer) {
      clearTimeout(registration.cleanupTimer);
      registration.cleanupTimer = null;
    }

    if (!registration.cleanup) {
      registration.cleanup = startRetryingNativeOAuthCallbacks(
        {
          addUrlOpenListener: (onUrl) =>
            CapacitorApp.addListener('appUrlOpen', ({ url }) => onUrl(url)),
          getLaunchUrl: async () => (await CapacitorApp.getLaunchUrl())?.url ?? null,
          closeBrowser: () => Browser.close(),
          navigate: (route) => nativeOAuthNavigateRef.current(route),
        },
        {
          onRegistrationError: (error) =>
            clientLogger.error('Failed to register native OAuth callbacks; retrying:', error),
        },
      );
    }
    const registrationCleanup = registration.cleanup;

    return () => {
      registration.leases = Math.max(0, registration.leases - 1);
      if (registration.leases !== 0) return;

      // StrictMode immediately reacquires this lease. Deferring cleanup one
      // task avoids installing two cold-launch listeners during its probe.
      registration.cleanupTimer = setTimeout(() => {
        registration.cleanupTimer = null;
        if (registration.leases !== 0 || registration.cleanup !== registrationCleanup) return;
        registration.cleanup = null;
        void registrationCleanup().catch((error) => {
          clientLogger.error('Failed to clean up native OAuth callbacks:', error);
        });
      }, 0);
    };
  }, []);

  useEffect(() => {
    void bootstrapAuthSession();
  }, []); // only validate session on app load, not after login/logout state changes

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth routes */}
        <Route
          path="/login"
          element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <AuthRoute>
              <ForgotPassword />
            </AuthRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <AuthRoute>
              <ResetPassword />
            </AuthRoute>
          }
        />
        <Route
          path="/set-password"
          element={
            <AuthRoute>
              <SetPassword />
            </AuthRoute>
          }
        />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/oauth/native/callback" element={<OAuthCallback />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/support" element={<Support />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <ErrorBoundary>
                <Dashboard />
              </ErrorBoundary>
            }
          />
          {/* Routes restricted to members and admins */}
          <Route
            path="my-tasks"
            element={
              <ProtectedRoute allowedRoles={routeRoles('myTasks')}>
                <ErrorBoundary>
                  <MyTasks />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="projects"
            element={
              <ProtectedRoute allowedRoles={routeRoles('projects')}>
                <ErrorBoundary>
                  <Projects />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Individual project view allows guests - backend enforces project-level permissions */}
          <Route
            path="projects/:projectId"
            element={
              <ProtectedRoute allowedRoles={routeRoles('project')}>
                <ErrorBoundary>
                  <ProjectView />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Project automation - restricted to admins and members */}
          <Route
            path="projects/:projectId/automation"
            element={
              <ProtectedRoute allowedRoles={routeRoles('projectAutomation')}>
                <ErrorBoundary>
                  <ProjectAutomation />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Project forms - public work forms builder */}
          <Route
            path="projects/:projectId/forms"
            element={
              <ProtectedRoute allowedRoles={routeRoles('projectForms')}>
                <ErrorBoundary>
                  <FormBuilder />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Routes accessible by all authenticated users */}
          {(['vendors', 'properties', 'serviceTypes'] as const).map((routeKey) => (
            <Route
              key={routeKey}
              path={`${routeKey === 'serviceTypes' ? 'service-types' : routeKey}/*`}
              element={
                <ProtectedRoute allowedRoles={routeRoles(routeKey)}>
                  <ErrorBoundary>
                    <VendorWorkspace />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
          ))}
          <Route
            path="teams"
            element={
              <ProtectedRoute allowedRoles={routeRoles('workgroups')}>
                <ErrorBoundary>
                  <Teams />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="search"
            element={
              <ProtectedRoute allowedRoles={routeRoles('search')}>
                <ErrorBoundary>
                  <SearchPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="notifications"
            element={
              <ProtectedRoute allowedRoles={routeRoles('inbox')}>
                <ErrorBoundary>
                  <Notifications />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="credentials"
            element={
              <ProtectedRoute allowedRoles={routeRoles('credentials')}>
                <ErrorBoundary>
                  <Credentials />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="settings"
            element={
              <ProtectedRoute allowedRoles={routeRoles('settings')}>
                <ErrorBoundary>
                  <Settings />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="activity-feed"
            element={
              <ProtectedRoute allowedRoles={routeRoles('activity')}>
                <ErrorBoundary>
                  <ActivityFeed />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="recurring-tasks"
            element={
              <ProtectedRoute allowedRoles={routeRoles('recurring')}>
                <ErrorBoundary>
                  <RecurringTasks />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="tasks/:taskId"
            element={
              <ErrorBoundary>
                <TaskDeepLink />
              </ErrorBoundary>
            }
          />
        </Route>

        {/* Public form submission (no auth required) */}
        <Route
          path="/forms/:formId/submit"
          element={
            <ErrorBoundary>
              <FormSubmit />
            </ErrorBoundary>
          }
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthQueryIsolation />
      <ThemeProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
        <ToastContainer />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
